import { useCallback, useMemo, useState } from "react";
import { addWeeks, parseISO } from "date-fns";
import { Plus, Users } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { isOverdue, mondayOf, prettyDate, todayIdx, weekLabel } from "@/lib/dates";
import { usePlanner } from "@/hooks/usePlanner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Header } from "@/components/planner/Header";
import { FiltersBar, DEFAULT_FILTERS } from "@/components/planner/FiltersBar";
import { WeekGrid } from "@/components/planner/WeekGrid";
import { MobileDayView } from "@/components/planner/MobileDayView";
import { UnassignedPanel } from "@/components/planner/UnassignedPanel";
import { OpenTasksView } from "@/components/planner/OpenTasksView";
import { TaskDialog } from "@/components/planner/TaskDialog";
import { AssignDialog } from "@/components/planner/AssignDialog";
import { EmployeesDialog } from "@/components/planner/EmployeesDialog";
import { CopyTaskDialog } from "@/components/planner/CopyTaskDialog";
import { VehiclesDialog } from "@/components/planner/VehiclesDialog";
import { DayShareDialog } from "@/components/planner/DayShareDialog";
import { AbsenceDialog } from "@/components/planner/AbsenceDialog";
import { dayShareText } from "@/lib/share";
import { sortTasks } from "@/lib/order";
import { toast } from "sonner";

const STATUS_TEST = { all: () => true, todo: (t) => !t.completed, done: (t) => t.completed, late: isOverdue };
const CLOSED_DIALOG = { open: false, task: null, preset: null };

const copyTitle = (copied) => {
  if (!copied) return "Attività già presente";
  return copied > 1 ? `${copied} copie create` : "Copia creata";
};

const assignTitle = (res, task) => {
  if (res.assigned > 1) return `${res.assigned} assegnazioni create`;
  if (res.recurring) return "Copia assegnata";
  return task.date ? "Attività riprogrammata" : "Attività assegnata";
};

const saveTitle = (id, res, recurring) => {
  if (res?.assigned && recurring) return `Ricorrente salvata e assegnata (${res.assigned})`;
  if (res?.assigned) return res.assigned > 1 ? `Attività creata e assegnata (${res.assigned})` : "Attività creata e assegnata";
  if (recurring) return "Ricorrente salvata";
  return id ? "Attività aggiornata" : "Attività creata";
};

const copyDescription = (res, otherWeek, targetMonday) => {
  if (res.skipped) return `${res.skipped} già presenti, non duplicate`;
  return otherWeek ? `Settimana ${weekLabel(targetMonday)}` : undefined;
};

const EmptyEmployees = ({ onAdd }) => (
  <div data-testid="no-employees" className="planner-surface flex flex-col items-center gap-3 py-16 text-center">
    <Users className="h-8 w-8 text-slate-400" />
    <p className="font-display text-base font-semibold">Nessun dipendente</p>
    <Button data-testid="no-employees-add-btn" onClick={onAdd} className="rounded-xl">Aggiungi dipendenti</Button>
  </div>
);

export default function Planner() {
  const p = usePlanner();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isWide = useMediaQuery("(min-width: 1024px)");
  const [view, setView] = useState("grid");
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, day: window.innerWidth < 768 ? todayIdx() : "all" }));
  const [taskDialog, setTaskDialog] = useState(CLOSED_DIALOG);
  const [assignTask, setAssignTask] = useState(null);
  const [empOpen, setEmpOpen] = useState(false);
  const [vehOpen, setVehOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [copyTask, setCopyTask] = useState(null);
  const [dayShare, setDayShare] = useState(null);
  const [absenceTarget, setAbsenceTarget] = useState(null);
  const { run } = p;

  const h = useMemo(() => ({
    absenceFor: (empId, date) => p.absences.find((a) => a.employee_id === empId && a.date === date),
    markAbsent: (employee, day) => setAbsenceTarget({ employee, day }),
    removeAbsence: (absence, employee, day) => run(() => api.deleteAbsence(absence.id), `${employee.name} di nuovo disponibile ${day.name.toLowerCase()}`),
    toggle: (t) => run(() => api.updateTask(t.id, { completed: !t.completed }), t.completed ? "Attività riaperta" : "Attività completata"),
    edit: (t) => setTaskDialog({ open: true, task: t, preset: null }),
    add: (employee_id, date, extra) => setTaskDialog({ open: true, task: null, preset: { employee_id, date, ...extra } }),
    assign: (t) => setAssignTask(t),
    copy: (t) => setCopyTask(t),
    shareDay: (employee, day) => setDayShare({ employee, day }),
    move: (id, employee_id, date) => {
      const t = [...p.weekTasks, ...p.unassigned, ...p.recurring].find((x) => x.id === id);
      // Dropped where it already is, or a recurring template back on the list: nothing to do
      if (t && (t.employee_id || null) === employee_id && (t.date || null) === date) return;
      if (t?.recurring && !employee_id) return;
      return employee_id
        ? run(() => api.assignTask(id, { employee_ids: [employee_id], dates: [date] }), t?.recurring ? "Copia assegnata" : "Attività assegnata")
        : run(() => api.updateTask(id, { employee_id: null, date: null }), "Attività spostata tra le da assegnare");
    },
    reorder: (ids) => run(() => api.reorderTasks({ ids })),
    resetOrder: (tasks) => run(() => api.reorderTasks({ ids: tasks.map((t) => t.id), reset: true }), "Ordinate per priorità"),
    toggleWeekly: (t) =>
      run(() => api.updateTask(t.id, { auto_weekly: !t.auto_weekly }), t.auto_weekly ? `"${t.title}" non si ripete più` : `"${t.title}" si ripete ogni settimana`),
  }), [run, p.absences, p.weekTasks, p.unassigned, p.recurring]);

  const assignMany = async (task, employee_ids, dates) => {
    let res;
    const ok = await run(async () => {
      res = await api.assignTask(task.id, { employee_ids, dates });
    });
    if (ok) toast.success(assignTitle(res, task), { description: res.skipped ? `${res.skipped} saltate (assente o già presente)` : undefined });
    return ok;
  };

  const confirmAbsence = async ({ employee }, start, end) => {
    let res;
    const ok = await run(async () => {
      res = await api.createAbsenceRange({ employee_id: employee.id, start, end });
    });
    if (ok) {
      const period = start === end ? prettyDate(start) : `dal ${prettyDate(start)} al ${prettyDate(end)}`;
      toast.success(`${employee.name} segnato assente ${period}`, {
        description: res.moved ? `${res.moved} attività ${res.moved === 1 ? "spostata" : "spostate"} in "Da assegnare"` : undefined,
      });
    }
    setAbsenceTarget(null);
  };

  const visibleDays = filters.day === "all" ? p.days : p.days.filter((d) => String(d.idx) === filters.day);
  const visibleEmployees = filters.employee === "all" ? p.employees : p.employees.filter((e) => e.id === filters.employee);
  const tasksFor = useCallback(
    (empId, date) => sortTasks(p.weekTasks.filter((t) => t.employee_id === empId && t.date === date && STATUS_TEST[filters.status](t))),
    [p.weekTasks, filters.status]
  );
  const openUnassigned = p.unassigned.filter((t) => !t.completed).length;
  const lateTasks = useMemo(() => p.openTasks.filter(isOverdue), [p.openTasks]);
  const shareTasks = dayShare ? sortTasks(p.weekTasks.filter((t) => t.employee_id === dayShare.employee.id && t.date === dayShare.day.iso)) : [];
  const sharePayload = shareTasks.length
    ? { subtitle: `${dayShare.employee.name} · ${dayShare.day.name} ${dayShare.day.label}`, text: dayShareText(dayShare.employee, dayShare.day.iso, shareTasks) }
    : null;
  const unassignedPanel = <UnassignedPanel tasks={p.unassigned} late={lateTasks} recurring={p.recurring} employees={p.employees} h={h} />;

  const saveTask = async (body, id, slots) => {
    let saved;
    let res;
    const ok = await run(async () => {
      saved = id ? await api.updateTask(id, body) : await api.createTask(body);
      // A recurring task with usual employees and days goes straight into the week on screen
      if (saved.recurring && saved.usual_days.length && saved.usual_employee_ids.length) {
        res = await api.assignRecurringWeek({ monday: p.days[0].iso, task_id: saved.id });
      }
      if (slots) {
        try {
          res = await api.assignTask(saved.id, slots);
        } catch (err) {
          // Nothing could be assigned: don't leave a stray copy in "Da assegnare"
          await api.deleteTask(saved.id);
          throw err;
        }
      }
    });
    if (ok) toast.success(saveTitle(id, res, saved.recurring), { description: res?.skipped ? `${res.skipped} già presenti o con dipendente assente` : undefined });
    return ok;
  };

  const copyToSlots = async (id, employee_ids, dates) => {
    let res;
    const ok = await run(async () => {
      res = await api.copyTask(id, { employee_ids, dates });
    });
    if (!ok) return false;
    const firstOther = [...dates].sort().find((d) => !p.days.some((x) => x.iso === d));
    const targetMonday = firstOther ? mondayOf(parseISO(firstOther)) : p.monday;
    const otherWeek = Boolean(firstOther);
    toast.success(copyTitle(res.copied), {
      description: copyDescription(res, otherWeek, targetMonday),
      action: res.copied && otherWeek ? { label: "Vai", onClick: () => p.setMonday(targetMonday) } : undefined,
    });
    return true;
  };

  const week = {
    label: weekLabel(p.monday),
    onPrev: () => p.setMonday((m) => addWeeks(m, -1)),
    onNext: () => p.setMonday((m) => addWeeks(m, 1)),
    onToday: () => p.setMonday(mondayOf(new Date())),
  };

  const openFilters = useMemo(() => ({ ...filters, status: "all" }), [filters]);

  const renderGrid = () => {
    if (!p.loading && p.employees.length === 0) return <EmptyEmployees onAdd={() => setEmpOpen(true)} />;
    if (isDesktop) return <WeekGrid days={visibleDays} employees={visibleEmployees} tasksFor={tasksFor} h={h} />;
    return <MobileDayView days={p.days} visibleDays={visibleDays} employees={visibleEmployees} tasksFor={tasksFor} dayFilter={filters.day} setDay={(day) => setFilters((f) => ({ ...f, day }))} h={h} />;
  };

  return (
    <div className="app-shell min-h-screen pb-24 md:pb-10">
      <Header
        week={week}
        view={view}
        setView={setView}
        openCount={p.openTasks.length}
        unassignedCount={openUnassigned + lateTasks.length}
        showUnassignedBtn={!isWide || view !== "grid"}
        onUnassigned={() => setSheetOpen(true)}
        onEmployees={() => setEmpOpen(true)}
        onVehicles={() => setVehOpen(true)}
        onNew={() => h.add(null, null)}
        onRefresh={async () => {
          if (await p.refresh()) toast.success("Dati aggiornati");
        }}
      />
      <main className="mx-auto flex max-w-[1600px] gap-6 px-3 py-4 sm:px-6 sm:py-6">
        {isWide && view === "grid" && (
          <aside className="w-80 shrink-0">
            <div className="sticky top-36">{unassignedPanel}</div>
          </aside>
        )}
        <section className="min-w-0 flex-1 space-y-4 fade-up">
          {view === "grid" && <FiltersBar filters={filters} setFilters={setFilters} employees={p.employees} days={p.days} showDay={isDesktop} />}
          {view === "open" && <FiltersBar filters={openFilters} setFilters={setFilters} employees={p.employees} days={p.days} showDay={false} />}
          {view === "grid" ? renderGrid() : <OpenTasksView tasks={p.openTasks} employees={p.employees} employeeFilter={filters.employee} h={h} />}
        </section>
      </main>

      {!isDesktop && (
        <button type="button" data-testid="fab-new-task" onClick={() => h.add(null, null)} className="fab" aria-label="Nuova attività">
          <Plus className="h-6 w-6" />
        </button>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side={isDesktop ? "right" : "bottom"} data-testid="unassigned-sheet" className={isDesktop ? "w-[380px] overflow-y-auto" : "max-h-[85vh] overflow-y-auto rounded-t-2xl"}>
          <SheetHeader className="sr-only"><SheetTitle>Attività da assegnare</SheetTitle></SheetHeader>
          <div className="pt-4">{sheetOpen && unassignedPanel}</div>
        </SheetContent>
      </Sheet>

      <TaskDialog
        state={taskDialog}
        onClose={() => setTaskDialog(CLOSED_DIALOG)}
        employees={p.employees}
        vehicles={p.vehicles}
        onSave={saveTask}
        monday={p.monday}
        onDelete={(id) => run(() => api.deleteTask(id), "Attività eliminata")}
        onCopy={(t) => {
          setTaskDialog(CLOSED_DIALOG);
          setCopyTask(t);
        }}
      />
      <AssignDialog
        task={assignTask}
        employees={p.employees}
        days={p.days}
        absenceFor={h.absenceFor}
        onClose={() => setAssignTask(null)}
        onAssign={assignMany}
      />
      <DayShareDialog share={sharePayload} onClose={() => setDayShare(null)} />
      <AbsenceDialog target={absenceTarget} onClose={() => setAbsenceTarget(null)} onConfirm={confirmAbsence} />
      <CopyTaskDialog task={copyTask} employees={p.employees} onClose={() => setCopyTask(null)} onCopy={copyToSlots} />
      <VehiclesDialog
        open={vehOpen}
        onOpenChange={setVehOpen}
        vehicles={p.vehicles}
        onCreate={(body) => run(() => api.createVehicle(body), "Automezzo aggiunto")}
        onUpdate={(id, body) => run(() => api.updateVehicle(id, { name: body.name, plate: body.plate }), "Automezzo aggiornato")}
        onDelete={(id) => run(() => api.deleteVehicle(id), "Automezzo eliminato")}
      />
      <EmployeesDialog
        open={empOpen}
        onOpenChange={setEmpOpen}
        employees={p.employees}
        onCreate={(body) => run(() => api.createEmployee(body), "Dipendente aggiunto")}
        onUpdate={(id, body) => run(() => api.updateEmployee(id, { name: body.name, role: body.role, color: body.color }), "Dipendente aggiornato")}
        onDelete={(id) => run(() => api.deleteEmployee(id), "Dipendente eliminato")}
        onReorder={(ids) => run(() => api.reorderEmployees({ ids }), "Ordine dei dipendenti aggiornato")}
      />
    </div>
  );
}
